import {observer} from 'mobx-react-lite'
import React, {useCallback, useEffect, useMemo, useRef, useState} from 'react'
import {Lang} from 'yougile-lang'
import {Hub} from 'yougile-model'
import {
  addingAllowed,
  deleteAllowed,
  renameAllowed
} from '@client/hub-actions-allowed'
import {useBoardStore} from '@components/board/board-store'
import {ColumnColorPicker} from '@components/column-color-picker/column-color-picker'
import {ColumnStore} from '@components/columns/column-store'
import {IconButton} from '@components/common/base/buttons'
import {Menu, MenuItemData} from '@components/common/base/menu'
import {Dropdown} from '@components/common/dropdown/dropdown'
import {useTimelineNewStore} from '@components/timeline-new/timeline-new-store'
import {ColumnId} from '@components/timeline-new/types'
import {cn} from '@containers/html-utils'
import {Column} from '@extensions/column'
import {TwCell} from '../containers'
import {useRowHovered} from '../use-row-hovered'
import {ColumnMenuProps} from './types'

const useColumnMenu = (group: Hub, path: string): MenuItemData[] => {
  const [, boardStore] = useBoardStore()
  const [columnStore] = useState(() => new ColumnStore(boardStore, group))
  useEffect(() => {
    columnStore.syncOnNew(group)
  }, [columnStore, group.id])
  const [, store] = useTimelineNewStore()
  const isMirror = Column.isMirror(group)

  return useMemo(() => {
    const items: MenuItemData[] = []

    items.push({
      id: 'rename',
      icon: 'IconEdit',
      title: Lang.msg.rename,
      onClick: () => store.startEditing(undefined, path, ColumnId.title),
      disabled: !renameAllowed(group.id)
    })

    items.push({
      id: 'duplicate',
      icon: 'IconCopy',
      title: Lang.msg.duplicate,
      onClick: columnStore.duplicate,
      disabled: !addingAllowed(group.location)
    })

    if (renameAllowed(group.id) && !isMirror) {
      items.push(
        {separator: true},
        {
          id: 'colorpicker',
          element: <ColumnColorPicker columnId={group.id} />
        }
      )
    }

    items.push(
      {separator: true},
      {
        id: 'delete',
        icon: 'IconTrash',
        color: 'error',
        title: Lang.msg.delete,
        onClick: columnStore.delete,
        disabled: !deleteAllowed(group.id)
      }
    )

    return items
  }, [columnStore, group.id, group.location, isMirror, path, store, boardStore])
}

export const ColumnMenuCell = observer(
  ({className, group, path}: ColumnMenuProps) => {
    const cellRef = useRef(null)
    const ref = useRef(null)
    const hovered = useRowHovered(cellRef)
    const [visible, setVisible] = useState(false)
    const open = useCallback(() => setVisible(true), [setVisible])
    const close = useCallback(() => setVisible(false), [setVisible])

    const menuItems = useColumnMenu(group, path)

    return (
      <>
        <TwCell ref={cellRef}>
          {(hovered || visible) && (
            <IconButton
              ref={ref}
              icon="IconDotsVertical"
              color="grey"
              size="small"
              wrapperSize="big"
              className={cn('relative', className)}
              forcedState={visible ? 'active' : undefined}
              onClick={open}
            />
          )}
        </TwCell>
        {visible && (
          <Dropdown
            reference={ref}
            hideArrow
            className="overflow-visible"
            placement="bottom-start"
            fallbackPlacements={['bottom-end', 'right-start', 'left-start']}
            content={<Menu onItemClicked={close} items={menuItems} />}
            visible
            onVisibleChange={close}
            theme="new-menu"
          />
        )}
      </>
    )
  }
)
